import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BookOpenCheck, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { Logo } from "@/components/Logo";
import { PatternBadge, TYPE_LABEL } from "./shared";

export default function MockTestLanding() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { api.get(`/test-prep/public/exams/${slug}`).then((r) => setD(r.data)).catch(() => setMissing(true)); }, [slug]);

  if (missing) return <Shell><p className="text-slate-400">This exam page is not available.</p></Shell>;
  if (!d) return <Shell><p className="text-slate-400">Loading...</p></Shell>;
  const { exam, subjects, test_types } = d;

  return (
    <Shell>
      <Seo title={`${exam.name} Mock Test ${exam.session}`} description={exam.seo.description} path={exam.seo.canonical} />
      <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-cyan-400">Test Preparation · Session {exam.session}</p>
      <h1 data-testid="landing-title" className="mt-2 text-4xl font-extrabold tracking-tight sm:text-5xl">{exam.name} Mock Test {exam.session}</h1>
      <p className="mt-3 max-w-2xl text-slate-400">Quick, topic-wise, chapter-wise, subject-wise and full mock tests for {exam.name} with deterministic performance analysis, weak-area detection and a direct path into EntranceZone AI Rank &amp; College Prediction.</p>
      <div className="mt-4"><PatternBadge status={exam.pattern_status} /></div>
      {exam.pattern && <p className="mt-2 text-xs text-slate-500">Mock pattern baseline: {exam.pattern.questions} questions · {exam.pattern.total_marks} marks · {exam.pattern.minutes} min · +{exam.pattern.marks_correct} / {exam.pattern.marks_wrong}</p>}
      <Link to="/auth" data-testid="landing-cta" className="mt-6 inline-flex items-center gap-2 rounded-full bg-cyan-400 px-7 py-3 text-sm font-bold text-[#041016] transition-transform hover:scale-105"><BookOpenCheck className="h-4 w-4" /> Start free practice <ArrowRight className="h-4 w-4" /></Link>

      <h2 className="mt-12 text-base font-bold md:text-lg">Test levels</h2>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {test_types.map((t) => <div key={t.id} className="rounded-2xl border border-white/8 bg-[#131C2E] p-4"><p className="font-semibold">{TYPE_LABEL[t.id]}</p><p className="mt-1 text-xs text-slate-400">{t.desc}</p></div>)}
      </div>

      <h2 className="mt-10 text-base font-bold md:text-lg">{exam.name} subject-wise mock tests</h2>
      <ul className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {subjects.map((s) => <li key={s.id}><Link to={s.url} data-testid={`landing-subject-${s.slug}`} className="block rounded-xl border border-white/8 bg-[#131C2E] px-4 py-3 text-sm transition-colors hover:border-cyan-400/40"><span className="font-medium">{exam.name} {s.name} Mock Test</span><span className="block text-xs text-slate-500">{s.question_count} practice questions</span></Link></li>)}
      </ul>
      <p className="mt-8 text-xs text-slate-600">Practice questions on this platform are clearly labelled by source. Sample practice content is not official or previous-year exam content. Rank/percentile outputs are estimates, never official results.</p>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#0B0F17] text-white">
    <header className="border-b border-white/8 px-6 py-4"><Logo /></header>
    <main className="mx-auto max-w-5xl px-6 py-12">{children}</main>
  </div>
);
